/**
 * ProfileSimulator - What-if simulation panel for Standards Profiles (T7.5)
 *
 * Shows a bar chart of standards count by category with included categories
 * highlighted and deprioritized ones dimmed. Displays projected effectiveness delta.
 *
 * Copyright (c) 2025-2026 Pareidolia LLC. All rights reserved.
 */

import React, { useMemo } from 'react';
import { STANDARD_CATEGORIES } from '../../api/standardsProfiles';

interface ProfileSimulatorProps {
  categoryIncludes: string[];
  categoryDeprioritizes: string[];
  priorityOverrides: Record<string, number>;
  currentEffectiveness?: number;
}

const ProfileSimulator: React.FC<ProfileSimulatorProps> = ({
  categoryIncludes,
  categoryDeprioritizes,
  priorityOverrides,
  currentEffectiveness,
}) => {
  const simulation = useMemo(() => {
    const categories = STANDARD_CATEGORIES.map(cat => {
      const isIncluded = categoryIncludes.includes(cat);
      const isDeprioritized = categoryDeprioritizes.includes(cat);
      const priority = priorityOverrides[cat] ?? (isIncluded ? 15 : isDeprioritized ? 5 : 10);

      return {
        name: cat,
        priority,
        status: isIncluded ? 'included' as const : isDeprioritized ? 'deprioritized' as const : 'default' as const,
      };
    });

    // Sort by priority descending
    categories.sort((a, b) => b.priority - a.priority);

    const maxPriority = Math.max(...categories.map(c => c.priority), 1);

    // Estimated effectiveness change based on configuration completeness
    const includedCount = categoryIncludes.length;
    const deprioritizedCount = categoryDeprioritizes.length;
    const configuredPct = (includedCount + deprioritizedCount) / STANDARD_CATEGORIES.length;
    const estimatedDelta = configuredPct > 0 ? Math.round(configuredPct * 15) - 5 : 0;

    return { categories, maxPriority, estimatedDelta, includedCount, deprioritizedCount };
  }, [categoryIncludes, categoryDeprioritizes, priorityOverrides]);

  const statusColor = {
    included: 'bg-green-500',
    deprioritized: 'bg-orange-300',
    default: 'bg-gray-300',
  };

  const statusLabel = {
    included: 'Boosted',
    deprioritized: 'Lowered',
    default: 'Default',
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">Profile Simulation</h4>

      {/* Summary metrics */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-green-50 rounded-lg p-2 text-center">
          <p className="text-lg font-bold text-green-700">{simulation.includedCount}</p>
          <p className="text-xs text-green-600">Boosted</p>
        </div>
        <div className="bg-orange-50 rounded-lg p-2 text-center">
          <p className="text-lg font-bold text-orange-700">{simulation.deprioritizedCount}</p>
          <p className="text-xs text-orange-600">Deprioritized</p>
        </div>
        <div className={`rounded-lg p-2 text-center ${simulation.estimatedDelta >= 0 ? 'bg-blue-50' : 'bg-red-50'}`}>
          <p className={`text-lg font-bold ${simulation.estimatedDelta >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
            {simulation.estimatedDelta >= 0 ? '+' : ''}{simulation.estimatedDelta}%
          </p>
          <p className={`text-xs ${simulation.estimatedDelta >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
            Est. Impact
          </p>
        </div>
      </div>

      {/* Bar chart */}
      <div className="space-y-1.5">
        {simulation.categories.map(cat => (
          <div key={cat.name} className="flex items-center gap-2">
            <span className="text-xs text-gray-600 w-32 truncate" title={cat.name}>
              {cat.name.replace(/-/g, ' ')}
            </span>
            <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${statusColor[cat.status]}`}
                style={{ width: `${(cat.priority / simulation.maxPriority) * 100}%` }}
              />
            </div>
            <span className="text-xs font-mono text-gray-500 w-6 text-right">{cat.priority}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded w-16 text-center
              ${cat.status === 'included' ? 'bg-green-100 text-green-700' :
                cat.status === 'deprioritized' ? 'bg-orange-100 text-orange-700' :
                'bg-gray-100 text-gray-500'}`}>
              {statusLabel[cat.status]}
            </span>
          </div>
        ))}
      </div>

      {currentEffectiveness !== undefined && (
        <p className="mt-3 text-xs text-gray-500">
          Current effectiveness: {currentEffectiveness.toFixed(1)}%
        </p>
      )}
    </div>
  );
};

export default ProfileSimulator;
